import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {of, Subject} from 'rxjs';
import {debounceTime, mergeAll, tap} from 'rxjs/operators';
import {ColumnFilter} from '../../columns-filters/column-filters.service';
import {ElasticValidator, ElasticValidatorFactory} from 'asax-elastic';
import {NgbPopover} from '@ng-bootstrap/ng-bootstrap';
import {Column} from 'asax-common';

const columnFilterDebounceTime = 500;

@Component({
  selector: 'asax-result-table-search-table-column-filter-input',
  templateUrl: './column-filter-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./column-filter-input.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ColumnFilterInputComponent implements OnChanges, OnInit, AfterViewInit {
  @ViewChild('popover', {static: true}) popover: NgbPopover;

  @Input() column: Column;

  @Output() outColumnFilter = new EventEmitter<ColumnFilter>();

  isInvalid: boolean;
  errorMessage: string;

  valueSubject = new Subject<string>();
  valueSubjectWithDebounceTime = new Subject<string>();

  elasticValidator: ElasticValidator;

  almaSourceNameColumnId = 'observationsSourceName';
  almaSourceFilterColumnTooltip: string = 'This is a column filter. It only applies to this tab and it is using a text match. You could use the full filter functionality from the upper left corner.';
  columnFilterTooltip: string = 'This is a column filter. It only applies to this tab.';

  constructor(private readonly ref: ChangeDetectorRef,
              private readonly elasticValidatorFactory: ElasticValidatorFactory,
              private readonly changeDetectorRef: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    this.elasticValidator = this.elasticValidatorFactory.getElasticValidator(this.column.elasticValidatorType);

    of(
      this.valueSubjectWithDebounceTime.asObservable().pipe(debounceTime(columnFilterDebounceTime)),
      this.valueSubject.asObservable()
    ).pipe(
      mergeAll(),
      tap(() => this.isInvalid = undefined),
      tap(() => this.validateValue()),
      tap(() => this.changeDetectorRef.markForCheck()),
      tap(() => this.emitFilter())
    ).subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.valueChanged();
  }

  ngAfterViewInit(): void {
    if (this.column?.value?.length > 0) {
      this.isInvalid = undefined;
      this.validateValue();
      this.emitFilter();
    }
  }

  valueChangedWithDebounceTime(): void {
    this.valueSubjectWithDebounceTime.next(undefined);
  }

  valueChanged(): void {
    this.valueSubject.next(undefined);
  }

  validateValue(): void {
    if (this.column?.value?.length > 0 && this.elasticValidator) {
      const {isInvalid, errorMessage} = this.elasticValidator.validate(this.column.value);
      this.isInvalid = isInvalid;
      this.errorMessage = errorMessage;
    }

    if (this.isInvalid) {
      this.popover?.open();
    } else {
      this.popover?.close();
    }
  }

  clearColumnFilterValue(): void {
    this.column.value = '';
    this.valueChanged();
  }

  emitFilter(): void {
    const filter = {
      id: this.column.id,
      value: this.column.value,
      valueParsed: this.column.value,
      label: this.column.name,
      isDisabled: this.column.isDisabled,
      isInvalid: this.isInvalid,
      elasticParserType: this.column.elasticParserType
    };

    this.outColumnFilter.emit(filter);
  }
}
